import React, { useCallback, useEffect, useRef } from 'react';
import Head from 'next/head';
import cn from 'classnames';
import Layout from 'components/layout';
import UseResize from 'utils/hooks/useResize';
import Contact from 'components/contact';
import TDK from 'components/tdk';
import { useState } from 'react';
import Button from 'components/button';
import style from './index.module.scss';
import CustomLink from 'components/link';

interface Props {
  headFixed?: boolean;
  headBg: string;
  headBgM?: string;
  title: string;
  simpleDesc: string;
  desc: string;
  pageTitle: string;
  tdkWord?: string;
  frameSrc: string;
  frameSrcM: string;
  caseList?: { logo: string; title: string; desc: string; link: string }[][];
  challenge: { icon: string; title: string; desc: string }[];
  advantage: { title: string; icon: string; desc: string }[];
  scene: { title: string; pcUrl: string; mUrl?: string; desc?: string }[];
  frame: { title: string; icon: string }[];
}
const IMG_PRE_PATH = 'https://sdk-source.xylink.com/h5/sdk_doc/static/website/solution/group/';

export const arrowIcon = [
  IMG_PRE_PATH + 'pc/c515db6e741040c0727791f18132f0ca5565c2cbcf8223339f990b2058e57726.png',
  IMG_PRE_PATH + 'pc/d5196741d402f021a87e049a9e4da1f7a7308c02907b20536c00ce03c9e026ee.png',
  IMG_PRE_PATH + 'pc/7291b3b9646024ce59bf0f0dfe3ab4bf1419de5a035f5a546409019e54f09ef8.png',
];

const SkeletonNew = (props: Props) => {
  const { headFixed = false, headBg, headBgM = '', caseList = [], scene, advantage, challenge, frame, frameSrc, frameSrcM, tdkWord, pageTitle, desc, title, simpleDesc } = props;
  const size = UseResize();
  const [currentPage, setCurrentPage] = useState(0);
  const currentDate = caseList[currentPage];
  const total = caseList.length;
  const stopAct = useRef(false);
  const container = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(0);
  const currentActive = currentDate[idx];
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tabs = (total > 1 && new Array(total).fill('')) || [];

  const next = () => {
    setCurrentPage((currentPage + 1) % total);
  };

  //eslint-disable-next-line react-hooks/exhaustive-deps
  const play = () => {
    clearTimer();
    if (stopAct.current) return;
    timerRef.current = setInterval(() => {
      // traverse to next page
      if (idx >= currentDate.length - 1) {
        setIdx(0);
        next();
      } else {
        setIdx(idx => idx + 1);
      }
    }, 3000);
  };

  const onScroll = useCallback(() => {
    const clientScreen = document.documentElement.clientHeight;
    if (container.current) {
      const { top } = container.current.getBoundingClientRect();
      /* 当元素出现在视口当中 */
      if (top < clientScreen) {
        play();
      }
    }
  }, [play]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimer();
    };
  }, [onScroll]);

  /* 移动端时 无滚动时 触发动画*/
  useEffect(() => {
    if (!container.current?.scrollTop) {
      onScroll();
    }
  }, [onScroll]);

  const clearTimer = () => {
    timerRef.current && clearInterval(timerRef.current);
  };

  return (
    <Layout layoutClass={cn(!headFixed && style.layout)} headerClass={cn(!headFixed && style.nav)}>
      {tdkWord ? (
        <TDK pageKey={tdkWord} />
      ) : (
        <Head>
          <title>小鱼易连-解决方案-{pageTitle}</title>
        </Head>
      )}
      <header style={{ backgroundImage: `url(${size.width < 768 ? headBgM ?? headBg : headBg})` }} className={style.header}>
        <div className={style.content}>
          <div className={style.mainTitle}>
            <p className={style.title}>{title}</p>
            <div className={style.simpleDesc}>
              <img className={style.arrowIcon} src={arrowIcon[0]} alt="" />
              <p className={style.descContent}>{simpleDesc}</p>
            </div>
          </div>
          <div className={style.mainContext}>
            <p className={cn(style.desc, 'for-pc')} dangerouslySetInnerHTML={{ __html: desc.replace(/\n/g, '<br/>') }}></p>
            <p className={cn(style.desc, 'for-mobile')} dangerouslySetInnerHTML={{ __html: desc.replace(/\n/g, '') }}></p>
            <Contact isTry={false} className={cn(style.contact, 'for-pc')} btnType={'newBtn'} btnWidth={190} />
            <Button category={'newBtn'} type="primary" href="/contact" className={cn(style.contact, 'for-mobile')}>
              联系销售
            </Button>
          </div>
        </div>
      </header>
      {/* 行业挑战 */}
      <div className={cn(style.challenge)}>
        <p className={style.sectionTitle}>行业挑战</p>
        <div className={style.challengeContent}>
          {challenge.map((item: any) => {
            return (
              <div key={item.title} className={cn(style.challengeItem, tdkWord === 'emergency' && style.special)}>
                <img src={item.icon} alt={item.title} />
                <div>
                  <p className={cn(style.itemTitle, 'for-pc')} dangerouslySetInnerHTML={{ __html: `${item.title}`.replace(/\n/g, '') }}></p>
                  <p className={cn(style.itemTitle, 'for-mobile', tdkWord === 'emergency' && style.twoLine)} dangerouslySetInnerHTML={{ __html: `${item.title}`.replace(/\n/g, '<br/>') }}></p>
                  <p className={style.itemDesc}>{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/*业务方案框架*/}
      <div className={style.frame} style={{ backgroundImage: `url(${size.width < 768 ? frameSrcM ?? frameSrc : frameSrc})` }}>
        <div className={style.content}>
          <p className={style.sectionTitle}>业务方案框架</p>
          <div className={style.frameContent}>
            {frame.map((item: any) => {
              return (
                <div key={item.title} className={style.frameItem}>
                  <img alt={item.title} src={item.icon} className={style.itemIcon} />
                  <p className={style.itemTitle}>{item.title}</p>
                </div>
              );
            })}
          </div>
          <div className={style.line}></div>
        </div>
      </div>
      {/* 应用场景 */}
      <div className={style.scene}>
        {/* 核心优势 */}
        <div className={style.advantage}>
          <div className={style.content}>
            <p className={style.sectionTitle}>核心优势</p>
            <div className={style.advantageContent}>
              {advantage.map((item: any) => {
                return (
                  <div key={item.title} className={style.advantageItem}>
                    <div className={style.advantageHeader}>
                      <img alt={item.title} src={item.icon} className={style.itemIcon} />
                      <p className={style.itemTitle}>{item.title}</p>
                    </div>
                    <p className={style.itemDesc} dangerouslySetInnerHTML={{ __html: item.desc.replace(/\n/g, '<br/>') }}></p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <p className={style.sectionTitle}>应用场景</p>
        <div className={style.sceneContent}>
          {scene.map((item: any) => {
            return (
              <div key={item.title} className={style.sceneItem}>
                <div className={style.left}>
                  <p className={style.itemTitle}>{item.title}</p>
                  <p className={style.itemDesc}>{item.desc}</p>
                  <img className={style.arrowIcon} src={arrowIcon[1]} alt="" />
                </div>
                <img className={style.pcUrl} src={item.pcUrl} alt={item.title} />
              </div>
            );
          })}
        </div>
      </div>
      {/*典型案例*/}
      <div className={style.case}>
        <p className={style.sectionTitle}>典型案例</p>
        <div className={style.caseContent} ref={container}>
          <div className={style.leftArea}>
            <div className={style.logos}>
              {currentDate.map((item: any, index: number) => {
                return (
                  <img
                    key={item.title}
                    className={cn(style.logo, index === idx && style.choose)}
                    src={item.logo}
                    alt={item.title}
                    onClick={() => {
                      stopAct.current = true;
                      setIdx(index);
                      clearTimer();
                    }}
                  />
                );
              })}
            </div>
            <div className={style.point}>
              {tabs.map((item: any, index: number) => {
                return (
                  <div
                    className={cn(style.pagePoint, index === currentPage && style.active)}
                    key={index}
                    onClick={() => {
                      setCurrentPage(index);
                      setIdx(0);
                    }}
                  ></div>
                );
              })}
            </div>
          </div>
          <CustomLink isNextLink={false} href={currentActive.link || ''} needTarget={true}>
            <div className={cn(style.mask, (tdkWord === 'medical' || tdkWord === 'finance') && style.decrease)}>
              <div className={style.introduce}>
                <div className={style.title}>{currentActive.title}</div>
                <div className={style.desc}>
                  {currentActive.desc}
                  <div></div>
                </div>
                <CustomLink isNextLink={false} href={currentActive.link || ''} needTarget={true} className={style.learnMore}>
                  了解更多 &gt;
                </CustomLink>
              </div>
            </div>
          </CustomLink>
        </div>

        <Contact className={cn(style['contact-footer'])} btnType={'newBtn'} />
        <img className={style.arrowIcon} src={arrowIcon[2]} alt="" />
      </div>
    </Layout>
  );
};

export default SkeletonNew;
